/**
 * DocImage — inline image node for PolicyEditor (TipTap). Content never stores a raw image
 * URL: the documents bucket is private, so a persisted URL would either need to be permanently
 * public or be a presigned one that silently goes dead on expiry. Instead the stored HTML only
 * ever carries `data-doc-image-id` (the id of a document row created via the existing
 * upload-url/confirm flow, entityType 'document-image') and `alt`; the backend sanitiser
 * enforces the same shape on save (see _safe_img_attrs in prod-nimbus-documents).
 *
 * Turning an id back into something the browser can show is a two-tier cache, not a
 * per-render fetch:
 * - blobUrls holds the instant local preview for an image the user just picked in this
 *   session (URL.createObjectURL on the File - the browser already has the bytes, no need to
 *   wait on a round trip to see it appear).
 * - resolvedUrls holds ids resolved via documentApi.resolveImageUrls for images that were
 *   already in the document's saved content when it was opened.
 *
 * A node view (not the schema's renderHTML) owns the live <img> element so the two never
 * collide: renderHTML - what editor.getHTML() serialises - never emits a src at all, while the
 * node view sets it imperatively from whichever cache has an answer. Updating an already-
 * mounted node view's src is a direct DOM write via domRegistry, not a ProseMirror transaction
 * - src is purely visual and doesn't belong in undo history.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/core';

export interface DocImageStorage {
  resolvedUrls: Map<string, string | null>;
  blobUrls: Map<string, string>;
  domRegistry: Map<string, Set<HTMLImageElement>>;
}

declare module '@tiptap/core' {
  interface Storage {
    docImage: DocImageStorage;
  }
}

function applyImageSrc(img: HTMLImageElement, url: string | null | undefined) {
  if (url) {
    img.src = url;
    img.classList.remove('np-doc-image-pending', 'np-doc-image-broken');
  } else if (url === null) {
    // Resolved, but the backend had nothing for this id (deleted image, foreign tenant, etc).
    img.removeAttribute('src');
    img.classList.remove('np-doc-image-pending');
    img.classList.add('np-doc-image-broken');
  } else {
    // Not resolved yet.
    img.classList.add('np-doc-image-pending');
  }
}

export const DocImage = Node.create<Record<string, never>, DocImageStorage>({
  name: 'docImage',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      docImageId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-doc-image-id'),
        renderHTML: (attributes: { docImageId?: string | null }) =>
          attributes.docImageId ? { 'data-doc-image-id': attributes.docImageId } : {},
      },
      alt: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('alt'),
        renderHTML: (attributes: { alt?: string | null }) => (attributes.alt ? { alt: attributes.alt } : {}),
      },
      // Block-level positioning only (margin, not float) - no text-wrap-around-image. Simpler
      // to implement and reason about than a real float layout, and covers the common case
      // (a screenshot or diagram sitting on its own line) that this editor's documents need.
      align: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const match = Array.from(element.classList).find((c) => c.startsWith('np-align-'));
          return match ? match.slice('np-align-'.length) : null;
        },
        renderHTML: (attributes: { align?: string | null }) =>
          attributes.align ? { class: `np-align-${attributes.align}` } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[data-doc-image-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },

  addStorage() {
    return {
      resolvedUrls: new Map(),
      blobUrls: new Map(),
      domRegistry: new Map(),
    };
  },

  addNodeView() {
    return ({ node, editor }) => {
      const img = document.createElement('img');
      img.className = 'np-doc-image';
      const docImageId = node.attrs.docImageId as string | null;
      const alt = node.attrs.alt as string | null;
      const align = node.attrs.align as string | null;
      if (alt) img.alt = alt;
      if (align) img.classList.add(`np-align-${align}`);

      const storage = editor.storage.docImage;
      if (docImageId) {
        if (!storage.domRegistry.has(docImageId)) storage.domRegistry.set(docImageId, new Set());
        storage.domRegistry.get(docImageId)!.add(img);
        const cached = storage.blobUrls.get(docImageId) ?? storage.resolvedUrls.get(docImageId);
        applyImageSrc(img, cached);
      } else {
        img.classList.add('np-doc-image-broken');
      }

      return {
        dom: img,
        destroy: () => {
          if (docImageId) storage.domRegistry.get(docImageId)?.delete(img);
        },
      };
    };
  },
});

/** Every data-doc-image-id in a stored content string - what needs resolving before display. */
export function extractImageIds(html: string): string[] {
  const ids = new Set<string>();
  const re = /data-doc-image-id="([0-9a-fA-F-]{36})"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) ids.add(match[1]);
  return Array.from(ids);
}

/** Push freshly-resolved URLs into an editor's image storage and update any already-mounted
 * <img> elements for them directly. */
export function registerResolvedImageUrls(editor: Editor, urls: Record<string, string | null>) {
  const storage = editor.storage.docImage;
  if (!storage) return;
  for (const [id, url] of Object.entries(urls)) {
    storage.resolvedUrls.set(id, url);
    if (storage.blobUrls.has(id)) continue; // a live local preview always wins
    storage.domRegistry.get(id)?.forEach((img) => applyImageSrc(img, url));
  }
}

/** Register an instant local preview for an image the user just picked, before its upload to
 * S3 has even finished - the browser already has the bytes, no need to wait on a round trip. */
export function registerBlobImageUrl(editor: Editor, id: string, blobUrl: string) {
  const storage = editor.storage.docImage;
  if (!storage) return;
  storage.blobUrls.set(id, blobUrl);
  storage.domRegistry.get(id)?.forEach((img) => applyImageSrc(img, blobUrl));
}
